import React, { useEffect, useState } from 'react'
import { Alert, Button, Col, Container, Form, Row, Spinner } from 'react-bootstrap'
import { useFormik } from 'formik'
import { EmailSchema } from '../../../utils/validation'
import PropTypes from 'prop-types'
import { capitalizeFirstLetter } from '../../../utils/helpers'
import isAuthenticated from '../../../routes/PrivateRoute'
import UpdateUserModal from '../../Modals/UpdateUserModal'

const Users = ({ users, getUsersState, createUser, createUserState, deleteUser, deleteUserState, resendSetup, resendSetupState, updateUser, updateUserState, forgotPassword, forgotPasswordState, reset }) => {
  const [alertState, setAlertState] = useState({
    show: false,
    variant: 'info',
    message: ''
  })

  const [selectedUser, setSelectedUser] = useState({})
  const [showModal, setShowModal] = useState(false)

  const handleOpenModal = (user) => {
    setSelectedUser(user)
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
  }

  const handleSubmitUpdateUser = (status) => {
    updateUser(selectedUser.userId, status)
  }

  useEffect(() => {
    setAlertState({
      show: false,
      variant: 'info',
      message: ''
    })
    
    return () => reset()
  }, [])

  useEffect(() => {
    if (createUserState.success) {
      setAlertState({ show: true, variant: 'success', message: 'Successfully created user' })
    }

    if (createUserState.error !== false) {
      setAlertState({ show: true, variant: 'danger', message: createUserState.error.message ? createUserState.error.message : createUserState.error })
    } 
  }, [createUserState.success, createUserState.error])

  useEffect(() => {
    if (updateUserState.success) {
      setAlertState({ show: true, variant: 'success', message: 'Updated user.' })
      setShowModal(false)
    }

    if (updateUserState.error !== false) {
      setAlertState({ show: true, variant: 'danger', message: updateUserState.error.message })
      setShowModal(false)
    }

  }, [updateUserState.success, updateUserState.error])

  useEffect(() => {
    if (deleteUserState.success) {
      setAlertState({ show: true, variant: 'success', message: 'Successfully removed user' })
    }

    if (deleteUserState.error !== false) {
      setAlertState({ show: true, variant: 'danger', message: deleteUserState.error.message ? deleteUserState.error.message : deleteUserState.error })
    } 
  }, [deleteUserState.success, deleteUserState.error])

  const handleResendSetup = (user) => {
    setSelectedUser(user)
    resendSetup(user.userId)
  }

  useEffect(() => {
    if (resendSetupState.success) {
      setAlertState({ show: true, variant: 'success', message: 'Successfully re-sent setup email.' })
    }

    if (resendSetupState.error !== false) {
      setAlertState({ show: true, variant: 'danger', message: 'Unable to resend setup email.' })
    }
  }, [resendSetupState.success, resendSetupState.error])

  const handleForgotPassword = (user) => {
    setSelectedUser(user)
    forgotPassword({email: user.email})
  }

  useEffect(() => {
    if (forgotPasswordState.success) {
      setAlertState({ show: true, variant: 'success', message: 'Successfully re-sent forgot password email.' })
    }

    if (forgotPasswordState.error !== false) {
      setAlertState({ show: true, variant: 'danger', message: 'Unable to resend forgot password email.' })
    }
  }, [forgotPasswordState.success, forgotPasswordState.error])

  const addUser = useFormik({
    initialValues: {
      email: ''
    },
    enableReinitialize: true,
    validationSchema: EmailSchema,
    onSubmit: (values) => {
      createUser(values)
    }
  })

  return (
    <Container fluid className='mx-3'>
      <Row>
        <Col md={6} sm={12}>
          <Form id='form' noValidate onSubmit={addUser.handleSubmit}>
            <Row>
              <Form.Group as={Col} sm={8}>
                <Form.Label>
                  Email
                </Form.Label>
                <Form.Control 
                  id='email'
                  name='email'
                  type='email'
                  onChange={addUser.handleChange}
                  onBlur={addUser.handleBlur}
                  isInvalid={!!addUser.errors.email && addUser.touched.email}
                  value={addUser.values.email}
                  autoComplete='email'
                  size='sm'
                />
                <Form.Control.Feedback type='invalid'>
                  {addUser.errors.email}
                </Form.Control.Feedback>
              </Form.Group>
              <Col sm={4}>
                <Button
                  type='submit'
                  className='w-100'
                  style={{ marginTop: '27px'}}
                  disabled={createUserState.loading}
                > 
                  {
                    createUserState.loading ? <Spinner animation='border' role='status' size='sm' /> : '+ Add New User'
                  }
                </Button>
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <Alert show={alertState.show} variant={alertState.variant} dismissible onClose={() => setAlertState((ps) => ({ ...ps, show: false }))}> 
                  {alertState.message}
                </Alert>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
      <Row className='table-header mt-2'>
        <Col xs={3}>
            User
        </Col>
      </Row>
      {
        getUsersState.loading ? (
          <Row className='mt-2 table-body'>
            <Col xs={12} className='text-center'>
              <Spinner
                as="span"
                animation="grow"
                size="sm"
                role="status"
                aria-hidden="true"
              />
              Loading...
            </Col>
          </Row>
        ) : users.length === 0 ? (
          <Row className='mt-2 table-body'>
            <Col xs={12} className='font-italic'>
              No Users.
            </Col>
          </Row>
        ) : users.map((user) => (
          <Row className='mt-2 table-body' key={user.userId}>
            <Col xs={3}>
              {user.email}
            </Col>
            <Col xs={2}>
              {
                user.status === 'ACTIVE' ? (
                  <div className='custom-control custom-switch'>
                    <input
                      type='checkbox'
                      className='custom-control-input'
                      id={user.email}
                      name={user.email}
                      checked={user.status === 'ACTIVE'}
                      onChange={() => handleOpenModal(user)}
                    />
                    <label
                      className='custom-control-label'
                      htmlFor={user.email}
                    >
                      {capitalizeFirstLetter(user.status)}
                    </label>
                  </div>
                ) : user.status === 'INACTIVE' ? (
                  <div className='custom-control custom-switch'>
                    <input
                      type='checkbox'
                      className='custom-control-input'
                      id={user.email}
                      name={user.email}
                      onChange={() => handleOpenModal(user)}
                      checked={user.status === 'ACTIVE'}
                    />
                    <label
                      className='custom-control-label'
                      htmlFor={user.email}
                    >
                      {capitalizeFirstLetter(user.status)}
                    </label>
                  </div>
                ) : <span className='font-italic'>Pending Setup</span>
              }
            </Col>
            <Col xs='auto'>
              {
                user.status === 'ACTIVE' 
                  ? (
                    <Button size='sm' variant='outline-primary' onClick={() => handleForgotPassword(user)}>Reset Password</Button>
                  ) : user.status === 'INACTIVE' 
                    ? null 
                    : (
                      <>
                        <Button 
                          size='sm' 
                          variant='outline-primary' 
                          onClick={() => handleResendSetup(user)}
                          disabled={selectedUser.userId === user.userId && resendSetupState.loading}
                          className='mr-2'
                        >
                          {
                            selectedUser.userId === user.userId && resendSetupState.loading 
                              ? <Spinner animation='border' role='status' size='sm' />
                              : 'Re-Send Setup Email'
                          }
                        </Button>
                        <Button 
                          size='sm' 
                          variant='outline-danger'
                          onClick={() => deleteUser(user.userId)}
                        >
                          Remove
                        </Button>
                      </>
                    )
              }
            </Col>
          </Row>
        ))
      }
      <UpdateUserModal
        user={selectedUser}
        show={showModal}
        onClose={handleCloseModal}
        onSubmit={handleSubmitUpdateUser}
        updateUserState={updateUserState}
      />
    </Container>
  )
}

Users.propTypes = {
  users: PropTypes.arrayOf(
    PropTypes.shape({
      userId: PropTypes.number,
      email: PropTypes.string,
      status: PropTypes.string
    })
  ),
  getUsersState: PropTypes.shape({
    loading: PropTypes.bool,
    success: PropTypes.bool,
    error: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.string,
      PropTypes.shape({
        message: PropTypes.string
      })
    ])
  }),
  createUser: PropTypes.func,
  createUserState: PropTypes.shape({
    loading: PropTypes.bool,
    success: PropTypes.bool,
    error: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.string,
      PropTypes.shape({
        message: PropTypes.string
      })
    ])
  }),
  updateUser: PropTypes.func,
  updateUserState: PropTypes.shape({
    loading: PropTypes.bool,
    success: PropTypes.bool,
    error: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.string,
      PropTypes.shape({
        message: PropTypes.string
      })
    ])
  }),
  deleteUser: PropTypes.func,
  deleteUserState: PropTypes.shape({
    loading: PropTypes.bool,
    success: PropTypes.bool,
    error: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.string,
      PropTypes.shape({
        message: PropTypes.string
      })
    ])
  }),
  resendSetup: PropTypes.func,
  resendSetupState: PropTypes.shape({
    loading: PropTypes.bool,
    success: PropTypes.bool,
    error: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.string,
      PropTypes.shape({
        message: PropTypes.string
      })
    ])
  }),
  forgotPassword: PropTypes.func,
  forgotPasswordState: PropTypes.shape({
    loading: PropTypes.bool,
    success: PropTypes.bool,
    error: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.string,
      PropTypes.shape({
        message: PropTypes.string
      })
    ])
  }),
  reset: PropTypes.func
}

export default isAuthenticated(Users)
